export const LEGAL_LAST_UPDATED = 'June 6, 2026';

export const PRIVACY_POLICY = {
  title: 'Privacy Policy',
  subtitle:
    'How Workloob collects, uses, and protects your information on our blockchain-powered freelance marketplace.',
  sections: [
    {
      id: 'introduction',
      title: '1. Introduction',
      paragraphs: [
        'Workloob Inc. ("Workloob," "we," "us," or "our") operates a global freelance marketplace that connects clients and talent across every industry — from creative work and technology to trades, consulting, and everyday professional services.',
        'Our platform uses blockchain technology for secure escrow payments, wallet-based identity, and community governance. This Privacy Policy explains what information we collect, how we use it, and the choices you have when using Workloob at workloob.com and related services.',
        'By creating an account or using Workloob, you agree to the practices described in this policy.',
      ],
    },
    {
      id: 'information-we-collect',
      title: '2. Information We Collect',
      paragraphs: ['We collect information you provide directly and information generated through your use of the platform:'],
      list: [
        'Account details: name, username, email address, profile photo, bio, skills, portfolio, and role (client or talent).',
        'Work activity: job and gig listings, applications, orders, messages, files, reviews, and dispute records.',
        'Payment & wallet data: connected wallet addresses, on-chain transaction hashes, escrow status, and USDC payment history linked to your account.',
        'Communications: support requests, governance participation, and newsletter subscriptions.',
        'Technical data: IP address, browser type, device information, and usage logs used to secure and improve the service.',
      ],
    },
    {
      id: 'how-we-use',
      title: '3. How We Use Your Information',
      paragraphs: ['We use collected information to:'],
      list: [
        'Operate the marketplace — matching clients with talent, processing orders, and enabling messaging.',
        'Facilitate blockchain escrow, contract interactions, and dispute resolution workflows.',
        'Verify identity, prevent fraud, and enforce our Terms of Use and Community Guidelines.',
        'Send service notifications, security alerts, and optional product updates.',
        'Improve platform performance, analytics, and user experience.',
        'Comply with legal obligations and respond to lawful requests.',
      ],
    },
    {
      id: 'blockchain',
      title: '4. Blockchain & On-Chain Data',
      paragraphs: [
        'Workloob integrates with public blockchains (including Base). When you connect a wallet or complete an on-chain action, certain data is recorded on a public ledger that we do not control.',
        'Blockchain data — such as wallet addresses, transaction amounts, and smart-contract events — may be visible to anyone and cannot be deleted from the chain. We link on-chain activity to your Workloob profile only where necessary to provide escrow, payouts, staking, and governance features.',
        'You are responsible for safeguarding your wallet credentials. Workloob never asks for your seed phrase or private keys.',
      ],
    },
    {
      id: 'sharing',
      title: '5. Information Sharing',
      paragraphs: [
        'We do not sell your personal information. We may share data with:',
      ],
      list: [
        'Other users, as part of normal marketplace activity (e.g., public profiles, proposals, and completed work).',
        'Infrastructure providers that host our application, send email, or process analytics — under confidentiality obligations.',
        'Law enforcement or regulators when required by applicable law.',
        'Successors in the event of a merger, acquisition, or asset sale, with notice where required.',
      ],
    },
    {
      id: 'security',
      title: '6. Data Security',
      paragraphs: [
        'We implement administrative, technical, and organizational measures designed to protect your information, including encryption in transit, access controls, and monitoring for suspicious activity.',
        'No online service is completely secure. Please use strong passwords, enable available security features, and report unauthorized access immediately via our Discord support channel.',
      ],
    },
    {
      id: 'your-rights',
      title: '7. Your Rights & Choices',
      paragraphs: ['Depending on your location, you may have the right to:'],
      list: [
        'Access, correct, or delete personal information we hold about you.',
        'Object to or restrict certain processing activities.',
        'Withdraw marketing consent at any time.',
        'Disconnect your wallet from your profile (on-chain history may remain public).',
      ],
      closingParagraphs: [
        'To exercise these rights, contact us through Discord or email at contact@workloob.com. We will respond within a reasonable timeframe.',
      ],
    },
    {
      id: 'cookies',
      title: '8. Cookies & Similar Technologies',
      paragraphs: [
        'We use cookies and local storage to keep you signed in, remember preferences (such as theme), and understand how the platform is used. You can control cookies through your browser settings; disabling them may limit some features.',
      ],
    },
    {
      id: 'children',
      title: '9. Children',
      paragraphs: [
        'Workloob is not intended for users under 18 years of age. We do not knowingly collect personal information from children. If you believe a minor has provided us data, please contact us so we can remove it.',
      ],
    },
    {
      id: 'changes',
      title: '10. Changes to This Policy',
      paragraphs: [
        'We may update this Privacy Policy from time to time. Material changes will be posted on this page with an updated effective date. Continued use of Workloob after changes take effect constitutes acceptance of the revised policy.',
      ],
    },
    {
      id: 'contact',
      title: '11. Contact Us',
      paragraphs: [
        'Questions about this Privacy Policy? Reach our team on Discord or email contact@workloob.com.',
      ],
    },
  ],
};

export const TERMS_OF_USE = {
  title: 'Terms of Use',
  subtitle:
    'The rules and agreements that govern your use of Workloob — a blockchain-powered marketplace for clients and talent worldwide.',
  sections: [
    {
      id: 'agreement',
      title: '1. Agreement to Terms',
      paragraphs: [
        'These Terms of Use ("Terms") form a binding agreement between you and Workloob Inc. when you access or use our platform, including websites, applications, smart contracts, and related services (collectively, the "Platform").',
        'If you do not agree to these Terms, you may not use Workloob. We may update these Terms periodically; your continued use after updates constitutes acceptance.',
      ],
    },
    {
      id: 'eligibility',
      title: '2. Eligibility & Accounts',
      paragraphs: [
        'You must be at least 18 years old and capable of entering a binding contract. You are responsible for maintaining accurate account information and keeping login credentials secure.',
        'Workloob supports dual roles — clients who hire and talent who deliver work. You may switch roles where the Platform allows, but you must act honestly in each capacity.',
        'One person may not maintain multiple accounts to manipulate reviews, bypass restrictions, or misrepresent identity.',
      ],
    },
    {
      id: 'services',
      title: '3. Platform Services',
      paragraphs: [
        'Workloob provides tools to post jobs and gigs, discover work, communicate, escrow funds on-chain, complete projects, leave reviews, and resolve disputes through community governance.',
        'We are a marketplace — not an employer, agency, or guarantor of any user\'s performance. Contracts for work are between clients and talent; Workloob facilitates connection, payment infrastructure, and dispute processes.',
        'Features may vary by region, account status, or network (testnet vs. mainnet). We may modify or discontinue features with reasonable notice where practicable.',
      ],
    },
    {
      id: 'responsibilities',
      title: '4. Client & Talent Responsibilities',
      paragraphs: ['All users agree to:'],
      list: [
        'Provide accurate descriptions of work, skills, deliverables, timelines, and budgets.',
        'Communicate professionally and respond to messages in a timely manner.',
        'Deliver work that matches agreed scope and quality, or pay for work that meets those standards.',
        'Use escrow and milestone flows as designed — not attempt to circumvent platform protections.',
        'Comply with applicable laws, tax obligations, and licensing requirements in your jurisdiction.',
      ],
    },
    {
      id: 'payments',
      title: '5. Payments, Escrow & USDC',
      paragraphs: [
        'Workloob uses smart-contract escrow on Base, typically denominated in USDC, to hold client funds until work milestones are met. Funding a project requires a compatible wallet and sufficient balance plus network gas fees.',
        'On-chain transactions are irreversible once confirmed. You are solely responsible for verifying wallet addresses, amounts, and network before signing transactions.',
        'Platform fees, where applicable, are disclosed before you confirm a transaction. Fee schedules may be updated with notice on the Platform or in documentation.',
      ],
    },
    {
      id: 'disputes',
      title: '6. Disputes & Governance',
      paragraphs: [
        'If a client and talent cannot resolve a disagreement directly, either party may escalate to Workloob\'s dispute and governance process. DAO members may vote on outcomes; authorized on-chain resolution may distribute escrowed USDC according to the final decision.',
        'You agree to participate in good faith, provide evidence when requested, and accept binding outcomes produced through Platform dispute procedures, subject to applicable law.',
      ],
    },
    {
      id: 'prohibited',
      title: '7. Prohibited Conduct',
      paragraphs: ['You may not use Workloob to:'],
      list: [
        'Engage in fraud, money laundering, sanctions evasion, or illegal services.',
        'Harass, threaten, discriminate against, or exploit other users.',
        'Post misleading listings, steal intellectual property, or deliver plagiarized work.',
        'Manipulate ratings, governance votes, or token mechanics.',
        'Attack, scrape, reverse-engineer, or disrupt Platform infrastructure.',
        'Circumvent escrow to move payments off-platform to avoid fees or protections.',
      ],
    },
    {
      id: 'ip',
      title: '8. Intellectual Property',
      paragraphs: [
        'Workloob retains all rights in the Platform, branding, software, and documentation. You retain ownership of content you create, but grant Workloob a license to host, display, and transmit it as needed to operate the service.',
        'Unless otherwise agreed in writing between client and talent, deliverables transfer to the client upon full payment and acceptance. Talent warrants they have the right to grant such rights.',
      ],
    },
    {
      id: 'termination',
      title: '9. Suspension & Termination',
      paragraphs: [
        'We may suspend or terminate accounts that violate these Terms, our Community Guidelines, or applicable law. You may close your account at any time; outstanding escrow obligations must be resolved first.',
        'Provisions that by nature should survive termination — including payment obligations, dispute outcomes, disclaimers, and limitations of liability — will remain in effect.',
      ],
    },
    {
      id: 'disclaimers',
      title: '10. Disclaimers & Limitation of Liability',
      paragraphs: [
        'THE PLATFORM IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DO NOT GUARANTEE UNINTERRUPTED SERVICE, ERROR-FREE SMART CONTRACTS, OR SPECIFIC EARNINGS OR HIRING OUTCOMES.',
        'TO THE MAXIMUM EXTENT PERMITTED BY LAW, WORKLOOB AND ITS AFFILIATES ARE NOT LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES, OR FOR LOSS OF PROFITS, DATA, OR DIGITAL ASSETS ARISING FROM YOUR USE OF THE PLATFORM OR BLOCKCHAIN NETWORKS.',
        'OUR AGGREGATE LIABILITY FOR ANY CLAIM RELATING TO THE PLATFORM SHALL NOT EXCEED THE GREATER OF (A) FEES YOU PAID TO WORKLOOB IN THE TWELVE MONTHS BEFORE THE CLAIM OR (B) ONE HUNDRED U.S. DOLLARS (US$100).',
      ],
    },
    {
      id: 'law',
      title: '11. Governing Law',
      paragraphs: [
        'These Terms are governed by the laws of the jurisdiction in which Workloob Inc. is incorporated, without regard to conflict-of-law principles. Disputes not subject to Platform governance may be brought in the courts of that jurisdiction unless otherwise required by mandatory local law.',
      ],
    },
    {
      id: 'contact',
      title: '12. Contact',
      paragraphs: [
        'For questions about these Terms, contact us on Discord or at contact@workloob.com.',
      ],
    },
  ],
};

export const COMMUNITY_GUIDELINES = {
  title: 'Community Guidelines',
  subtitle:
    'Standards that keep Workloob safe, fair, and welcoming for every profession — powered by trust and blockchain transparency.',
  sections: [
    {
      id: 'promise',
      title: '1. Our Community Promise',
      paragraphs: [
        'Workloob exists so people from every walk of life can find meaningful work and hire skilled talent with confidence. Whether you design apps, write copy, provide labor services, consult on strategy, or offer specialized craft — you belong here.',
        'These Community Guidelines apply to everyone: clients, talent, DAO members, and visitors. They work alongside our Terms of Use and Privacy Policy. Violations may result in warnings, restrictions, or permanent removal.',
      ],
    },
    {
      id: 'respect',
      title: '2. Respect & Professionalism',
      paragraphs: ['Treat others as you would in a professional workplace:'],
      list: [
        'Use inclusive language; do not discriminate based on race, gender, religion, nationality, disability, age, or orientation.',
        'No harassment, bullying, hate speech, or sexual content in profiles, messages, or listings.',
        'Respect time zones and boundaries — not everyone is available around the clock.',
        'Disagree constructively; personal attacks are never acceptable.',
      ],
    },
    {
      id: 'honesty',
      title: '3. Honest Listings & Work',
      paragraphs: [
        'Clients must describe projects accurately — scope, budget, timeline, and requirements should reflect what you actually need.',
        'Talent must represent skills, experience, and portfolio work truthfully. Do not claim credit for work you did not perform.',
        'Fake reviews, sock-puppet accounts, and artificial engagement undermine the community and are strictly prohibited.',
      ],
    },
    {
      id: 'communication',
      title: '4. Communication Standards',
      paragraphs: [
        'Keep project communication on Workloob where possible so escrow and dispute tools can protect both parties.',
        'Respond to messages within a reasonable time. If you cannot continue a project, say so clearly and promptly.',
        'Do not spam users with unsolicited offers, external links to unsafe sites, or phishing attempts.',
      ],
    },
    {
      id: 'payments',
      title: '5. Payments & Escrow',
      paragraphs: [
        'Use Workloob\'s escrow and milestone flows for platform projects. Asking clients to pay entirely off-platform before work begins removes protections for both sides.',
        'Understand that blockchain payments use real digital assets (USDC). Double-check amounts and wallet addresses before confirming.',
        'Clients should fund escrow as agreed; talent should not begin substantial work without confirmed escrow where required.',
      ],
    },
    {
      id: 'reviews',
      title: '6. Reviews & Reputation',
      paragraphs: [
        'Leave honest, specific feedback after completed work. Reviews should focus on the project — not personal grudges.',
        'Do not trade positive reviews, threaten negative reviews for concessions, or retaliate against fair criticism.',
      ],
    },
    {
      id: 'disputes',
      title: '7. Disputes & Escalation',
      paragraphs: [
        'Attempt to resolve disagreements directly first. Share evidence calmly and propose reasonable solutions.',
        'When escalation is needed, use Workloob\'s dispute process. Provide truthful documentation; false claims harm the entire community.',
        'DAO members participating in governance must act impartially, disclose conflicts of interest, and follow Platform rules.',
      ],
    },
    {
      id: 'content',
      title: '8. Content & Safety',
      paragraphs: ['Do not post or request work involving:'],
      list: [
        'Illegal goods, services, or activity in any jurisdiction.',
        'Malware, credential theft, or unauthorized access to systems.',
        'Adult content involving minors or non-consensual material.',
        'Weapons, controlled substances, or violence facilitation.',
        'Content that infringes copyrights, trademarks, or trade secrets.',
      ],
    },
    {
      id: 'enforcement',
      title: '9. Enforcement',
      paragraphs: [
        'Workloob may remove content, freeze escrow actions, restrict features, or ban accounts that violate these guidelines. Severity and history inform our response.',
        'We may cooperate with law enforcement where illegal activity is suspected. Users who believe action was taken in error may appeal through Discord support.',
      ],
    },
    {
      id: 'reporting',
      title: '10. Reporting Issues',
      paragraphs: [
        'See something that violates these guidelines? Report it via Discord or contact@workloob.com. Include usernames, links, and a clear description.',
        'Thank you for helping build a marketplace where talent everywhere can thrive — secured by professionalism and blockchain-powered trust.',
      ],
    },
  ],
};
